import { useState } from "react";
import { useAppContext } from "../../../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../../../controller/article/ArticleController";
import { AuthorController, AuthorControllerType } from "../../../../../controller/author/AuthorController";
import AuthorDto from "../../../../../model/author/AuthorDto";
import ArticleDto from "../../../../../model/manuscript/ArticleDto";
import { handleErrorMessage, parseArticleForBackend, useEffectOnce } from "../../../../../utils/Utils";

interface AuthorServiceType {
  openModal: any;
  closeModal: any;
  deleteAuthor: any;
  addAuthor: any;
  shiftUpElement: any;
  shiftDownElement: any;
  searchAuthorList: any;
  setSearchAuthorList: any;
  selectedSearchListAuthor: AuthorDto | undefined;
  setSelectedSearchListAuthor: any;
  selectedAddedListAuthor: any;
  setSelectedAddedListAuthor: any;
  addedAuthorList: any;
  setAddedAuthorList: any;
  open: any;
  setOpen: any;
  setForceRender: any;
  forceRender: any;
  authorSave: any;
  articleSave: any;
  getSearchAuthorList: any;
  parseAuthorName: any;
  searchDisable: boolean;
  onEnterSearch: any;
}

export default function AuthorService({ article }: any): AuthorServiceType {
  const { axiosGetSearchAuthorList }: AuthorControllerType = AuthorController();
  const { axiosAuthorSave, axiosArticleUpdate }: ArticleControllerType = ArticleController();

  const [open, setOpen] = useState(false);
  const [searchDisable, setSearchDisable] = useState(false);
  const [selectedSearchListAuthor, setSelectedSearchListAuthor] = useState<AuthorDto | undefined>();
  const [selectedAddedListAuthor, setSelectedAddedListAuthor] = useState<any>();
  const [searchAuthorList, setSearchAuthorList] = useState<any>();
  const [addedAuthorList, setAddedAuthorList] = useState<any>([]);
  const [forceRender, setForceRender] = useState<any>(1);
  const { showMessage, setBlockUI } = useAppContext();

  useEffectOnce(() => {
    setAddedAuthorList(article.articleAuthors);
  });

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const deleteAuthor = (setList: any, list: any, author: any) => {
    setList(list.filter((item: any) => item !== author));
  };
  const addAuthor = (author: any) => {
    setAddedAuthorList([...addedAuthorList, author]);
    deleteAuthor(setSearchAuthorList, searchAuthorList, author);
    setSelectedSearchListAuthor(undefined);
    closeModal();
    setSearchAuthorList([]);
  };
  const getSearchAuthorList = (searchParams: any) => {
    setBlockUI(true);
    let dataToSend: any = {};
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== "") dataToSend[key] = value;
    }
    setSearchDisable(true);
    axiosGetSearchAuthorList(dataToSend)
      .then((result: any) => {
        setSearchAuthorList(result.data);
        setSearchDisable(false);
        setBlockUI(false);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
        setSearchDisable(false);
        setBlockUI(false);
      });
  };

  const swapArrayItems = (niz: any, a: any, b: any) => {
    let temp = niz[b];
    niz[b] = niz[a];
    niz[a] = temp;
    return niz;
  };
  const shiftUpElement: any = (array: any, element: any) => {
    let selectedRowIndex = array.indexOf(element);
    let targetRowIndex = selectedRowIndex - 1;
    if (selectedRowIndex === 0) {
      array = swapArrayItems(array, array.length - 1, 0);
      setAddedAuthorList(array);
    } else {
      array = swapArrayItems(array, targetRowIndex, selectedRowIndex);
      setAddedAuthorList(array);
    }
    return array;
  };

  const shiftDownElement = (array: any, element: any) => {
    let selectedRowIndex = array.indexOf(element);
    let targetRowIndex = selectedRowIndex + 1;
    if (selectedRowIndex === array.length - 1) {
      array = swapArrayItems(array, 0, array.length - 1);
    } else {
      array = swapArrayItems(array, targetRowIndex, selectedRowIndex);
    }
    return array;
  };

  const articleSave = (values: any, article: any, setArticle: any, handleNext: any) => {
    const mergeArticle = {
      ...article,
      ...values,
      authorsIds: addedAuthorList.map((a: any) => a.id),
    };
    const newArticle = parseArticleForBackend(mergeArticle);
    axiosArticleUpdate(newArticle)
      .then((response: any) => {
        setArticle((article: ArticleDto) => {
          return {
            ...article,
            id: response.data.id,
            articleAuthors: response.data.articleAuthors,
            ...values,
          };
        });
        handleNext();
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const authorSave = (newAuthor: AuthorDto, setArticle: any) => {
    const authorToSend = {
      firstName: newAuthor.firstName,
      middleName: newAuthor?.middleName,
      lastName: newAuthor?.lastName,
      institute: newAuthor?.institute,
      department: newAuthor?.department,
      country: newAuthor?.country,
      orcid: newAuthor?.orcid,
      city: newAuthor?.city,
      email: newAuthor?.email,
    };
    axiosAuthorSave(authorToSend)
      .then((response: any) => {
        const authorID = response.data.id;
        addAuthor(response.data);
        setArticle((article: ArticleDto) => {
          return { ...article, authorsIds: [...article.authorsIds, authorID] };
        });
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const parseAuthorName = (author: AuthorDto) => {
    return `${author.firstName ?? ""} ${author.lastName ?? ""}`;
  };

  const onEnterSearch = (e: any, searchParams: any) => {
    if (e.key === "Enter") {
      getSearchAuthorList(searchParams);

      e.preventDefault();
    }
  };

  return {
    openModal,
    closeModal,
    deleteAuthor,
    addAuthor,
    shiftUpElement,
    shiftDownElement,
    searchAuthorList,
    setSearchAuthorList,
    selectedSearchListAuthor,
    setSelectedSearchListAuthor,
    selectedAddedListAuthor,
    setSelectedAddedListAuthor,
    addedAuthorList,
    setAddedAuthorList,
    open,
    setOpen,
    setForceRender,
    forceRender,
    authorSave,
    articleSave,
    getSearchAuthorList,
    parseAuthorName,
    searchDisable,
    onEnterSearch,
  };
}

export type { AuthorServiceType };
